import { useCallback, useMemo, useState } from 'react';
import type { SceneOrigin } from '@/types/sceneTypes';
import { splitDescription } from '@/modules/library/utils/storyAttributionUtils';

export interface StoryFormValues {
  title: string;
  description: string;
  latitude: string;
  longitude: string;
  address: string;
  city: string;
}

const EMPTY: StoryFormValues = { title: '', description: '', latitude: '', longitude: '', address: '', city: '' };

export function storyFormValues(origin: SceneOrigin): StoryFormValues {
  const location = origin.sceneOriginLocationInfo;
  return {
    title: origin.sceneOriginName || '',
    description: splitDescription(origin.sceneOriginDescription || '').userText,
    latitude: location?.coordinates?.lat?.toString() ?? '',
    longitude: location?.coordinates?.lng?.toString() ?? '',
    address: location?.address ?? '',
    city: location?.city || '',
  };
}

const sameLocation = (a: StoryFormValues, b: StoryFormValues) =>
  a.latitude === b.latitude && a.longitude === b.longitude && a.address === b.address && a.city === b.city;

const sameValues = (a: StoryFormValues, b: StoryFormValues) =>
  a.title === b.title && a.description === b.description && sameLocation(a, b);

// A field takes the new stored value only while it still shows the value it was last filled with; location moves as one
function reseedClean(current: StoryFormValues, seeded: StoryFormValues, source: StoryFormValues): StoryFormValues {
  const next = { ...current };
  if (current.title === seeded.title) next.title = source.title;
  if (current.description === seeded.description) next.description = source.description;
  if (sameLocation(current, seeded)) {
    next.latitude = source.latitude;
    next.longitude = source.longitude;
    next.address = source.address;
    next.city = source.city;
  }
  return next;
}

const norm = (v: string | null | undefined) => (v === 'null' || v == null ? '' : String(v));

function parsedLocation(values: StoryFormValues) {
  if (!values.latitude || !values.longitude) return null;
  const lat = parseFloat(values.latitude);
  const lng = parseFloat(values.longitude);
  return isNaN(lat) || isNaN(lng) ? null : { address: values.address || null, coordinates: { lat, lng } };
}

export function storyLocationChanged(origin: SceneOrigin, values: StoryFormValues): boolean {
  const raw = origin.sceneOriginLocationInfo;
  // No stored coordinates reads as no location, as the form fills blank lat/lng for it
  const original = raw && raw.coordinates?.lat != null && raw.coordinates?.lng != null ? raw : null;
  const current = parsedLocation(values);
  if (!original || !current) return Boolean(original) !== Boolean(current);
  return (
    norm(original.address) !== norm(current.address) ||
    norm(original.city) !== norm(values.city) ||
    Math.abs((original.coordinates?.lat ?? 0) - current.coordinates.lat) > 0.00001 ||
    Math.abs((original.coordinates?.lng ?? 0) - current.coordinates.lng) > 0.00001
  );
}

export function storyTextChanged(origin: SceneOrigin, values: StoryFormValues) {
  return {
    title: (origin.sceneOriginName || '') !== values.title,
    description: splitDescription(origin.sceneOriginDescription || '').userText !== values.description,
  };
}

export function useStoryForm(sceneOrigin: SceneOrigin | null) {
  const [values, setValues] = useState<StoryFormValues>(EMPTY);
  const [seeded, setSeeded] = useState<{ originId: string | null; values: StoryFormValues }>({ originId: null, values: EMPTY });

  if (sceneOrigin) {
    const source = storyFormValues(sceneOrigin);
    if (seeded.originId !== sceneOrigin.sceneOriginId) {
      setSeeded({ originId: sceneOrigin.sceneOriginId, values: source });
      setValues(source);
    } else if (!sameValues(seeded.values, source)) {
      const previous = seeded.values;
      setSeeded({ originId: sceneOrigin.sceneOriginId, values: source });
      setValues((current) => reseedClean(current, previous, source));
    }
  }

  // Fills every field, edited or not: the user's own write just stored these values
  const adopt = useCallback((origin: SceneOrigin) => {
    const source = storyFormValues(origin);
    setSeeded({ originId: origin.sceneOriginId, values: source });
    setValues(source);
  }, []);

  const setters = useMemo(() => {
    const field = (key: keyof StoryFormValues) => (value: string) =>
      setValues((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
    return {
      setTitle: field('title'),
      setDescription: field('description'),
      setLatitude: field('latitude'),
      setLongitude: field('longitude'),
      setAddress: field('address'),
      setCity: field('city'),
    };
  }, []);

  const changes = sceneOrigin ? storyTextChanged(sceneOrigin, values) : { title: false, description: false };
  const locationChanged = sceneOrigin ? storyLocationChanged(sceneOrigin, values) : false;

  return {
    values,
    adopt,
    ...setters,
    titleChanged: changes.title,
    descriptionChanged: changes.description,
    locationChanged,
    dirty: changes.title || changes.description || locationChanged,
  };
}
